import { isClerkAPIResponseError, isClerkRuntimeError } from "@clerk/clerk-react/errors";
import axios from "axios";
import { GENERIC_SERVER_ERROR_MESSAGE } from "@/constants/errors";
import { asEntitlementRefusal, refusalMessage } from "@/lib/entitlementError";

function nonEmpty(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * The one mapping from a caught value to the sentence a user sees on an onboarding step or the card
 * form (GFG-427).
 *
 * Only texts written for a human are safe to show: Clerk's own `longMessage`, the server's `error`
 * sentence, an entitlement refusal's plan copy, and the plain `Error` sentences our own client and
 * API wrappers throw. Everything else is a runtime detail and gets the fallback instead: a Clerk
 * network error, an axios "Network Error" or "Request failed with status code 500", a TypeError
 * such as "Cannot read properties of undefined". Showing those leaks internals and tells the user
 * nothing they can act on.
 */
export function userFacingErrorMessage(
  err: unknown,
  fallback: string = GENERIC_SERVER_ERROR_MESSAGE,
): string {
  // Clerk's type guards throw on any falsy value (null, undefined, 0, "") instead of answering false.
  if (!err) return fallback;

  if (isClerkAPIResponseError(err)) {
    const first = err.errors[0];
    return nonEmpty(first?.longMessage) ?? nonEmpty(first?.message) ?? fallback;
  }
  if (isClerkRuntimeError(err)) return fallback;

  if (axios.isAxiosError(err)) {
    if (!err.response) return fallback;
    const refusal = asEntitlementRefusal(err);
    if (refusal) return refusalMessage(refusal);
    const body = err.response.data as { error?: unknown } | null | undefined;
    return nonEmpty(body?.error) ?? fallback;
  }

  // Exact name match: TypeError and friends are Errors too, but their text is not for users.
  if (err instanceof Error && err.name === "Error") return nonEmpty(err.message) ?? fallback;

  return fallback;
}
